import { useEffect, useMemo, useState } from 'react'
import { convertSearchSpringProductPricingToMarketPrice, getCarouselItems, isSearchSpringCarouselProductResponse, isSearchSpringProductResponse, standardSearch } from '../../helpers/searchSpringHelpers'
import { Facet, Pagination, Payload, Query, Result, SearchSpringProductResponse, Sorting, SSCarouselResult } from '../../search-spring/types/types'
import { extractFilterParamsFromURL } from '../helpers/extractParamsFromURL'
import PaginationService from './PaginationService'
import useWindowSize from '../../search-spring/customHooks/useWindowSize'
import SearchPageHeader from './SearchPageHeader'
import ProductCarousel from '../../product-carousel/components/ProductCarousel'
import { getCookie } from '../../helpers/cookies'

/*
  Search Page control center
*/

declare var Shopify: { currency: { active: string } }

function SearchPageInit() {
  const windowSize = useWindowSize()
  const resultsPerPage = windowSize.width <= 640 ? CD_STATE.searchspring.numberOfProductsMobile : CD_STATE.searchspring.numberOfProductsDesktop
  const [query, setQuery] = useState<Query>({ corrected: '', matchType: '', original: '' })
  const [activeFilters, setActiveFilters] = useState<number>(0)
  const [products, setProducts] = useState<Result[]>([])
  const [pagination, setPagination] = useState<Pagination>()
  const [filters, setFilters] = useState<Facet[]>()
  const [sorting, setSorting] = useState<Sorting>()
  const [payload, setPayload] = useState<Payload>({})
  const [error, setError] = useState(false)
  const [recommendations, setRecommendation] = useState<SSCarouselResult[]>([])
  const [preventSearch, setPreventSearch] = useState(false)

  const searchQuery: string = useMemo(() => {
    const queryParams = new URLSearchParams(window.location?.search)
    return queryParams.get('q') || ''
  }, [window.location?.search])

  const getSearchPageResults = async () => {
    const filterAndPageParams = extractFilterParamsFromURL()
    try {
      // Payload won't update in time so we set it again here then update it.
      const updatedPayload: Payload = {
        ...payload,
        resultsPerPage,
        activeFilters: filterAndPageParams.filterArray
      }

      if (filterAndPageParams.page) {
        /*
          This means a url was pasted in as a page param can only ever exist in this context,
          so we will append it to our payload. For the first search we also need to adjust the 
          amount of products we get so it matches the page  
        */
        updatedPayload.page = filterAndPageParams.page
      } else {
        delete updatedPayload.page
      }
      setPayload(updatedPayload)

      /*
        initPageParam - is used to multiply the result per page by the number of the page param in the URL.
        Because on the first load if there is page=3 in the url then we expect 60 products on desktop, 
        however as default we always 20 (Or whatever S&S sets in the theme customize) products per search.  
      */
      const response = await standardSearch({ searchQuery, payload: updatedPayload, initPageParam: updatedPayload.page ?? null })
      if (isSearchSpringProductResponse(response)) {
        if (Shopify.currency.active != "AUD" || response.results.some(product => Number(product.price) === 0)) {
          const newproductData: SearchSpringProductResponse = await convertSearchSpringProductPricingToMarketPrice(response, Shopify.currency.active === "AUD")
          setProducts(newproductData?.results || response.results)
        } else {
          setProducts(response.results)
        }
        setQuery(response.query)
        setPagination(response.pagination)
        setFilters(response.facets)
        setSorting(response.sorting)
        setActiveFilters(response.filterSummary.length)
        getRecommendations()

        // Update the URL with the corrected query without triggering a new search
        if (response.query.corrected) {
          setPreventSearch(true) 
          const params = new URLSearchParams(window.location.search)
          params.set('q', response.query.corrected)
          window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`) 
        }
      } else {
        setError(true)
        console.error('Unexpected response structure:', response)
      }
    } catch (error) {
      setError(true)
      console.error('Error fetching search page results:', error)
    }
  }

  const getRecommendations = async () => {
    const ssViewedProducts = getCookie('ssViewedProducts')
    const ssShopperId = getCookie('ssShopperId')
    const ssCartProducts = getCookie('ssCartProducts')
    
    const payload = {
      limits: "5",
      tags: CD_STATE.searchspring.searchResultRecommendationProfile
    } as Payload

    if (ssViewedProducts) {
      payload.lastViewed = ssViewedProducts
    }

    if (ssShopperId) {
      payload.shopper = ssShopperId
    }

    if (ssCartProducts) {
      payload.cart = ssCartProducts
    }
    
    const response = await getCarouselItems(payload)
    if (isSearchSpringCarouselProductResponse(response)) {
      setRecommendation(response[0].results)
    } else {
      setError(true)
    }
  }

  useEffect(() => {
    const handleUrlChange = () => {
      // When doing a search by a filter need to delete the page params as page should reset when touching the filters
      const params = new URLSearchParams(new URL(location.href).search)
      const url = new URL(location.href)
      if (params.has('page')) {
        params.delete('page')
        history.replaceState(null, '', url.pathname + '?' + params.toString())
      }
      getSearchPageResults()
    }

    window.addEventListener('urlChange', handleUrlChange as EventListener)

    return () => {
      window.removeEventListener('urlChange', handleUrlChange as EventListener)
    }
  }, [])

  useEffect(() => {
    if (preventSearch) {
      setPreventSearch(false)
      return 
    }

    getSearchPageResults()
  }, [searchQuery])

  const ComponentHandler = (): JSX.Element => {
    if (products && pagination && filters && sorting) {
      return (
        <div>
          <SearchPageHeader totalResults={pagination.totalResults} searchQuery={query.corrected || searchQuery} filters={filters} sorting={sorting} triggerSearch={getSearchPageResults} activeFilters={activeFilters} emptyProduct={products.length === 0} />
          {
            pagination.totalResults > 0 ? (
              <PaginationService pagination={pagination} products={products} searchQuery={searchQuery} payload={payload} />
            ) : (
              recommendations.length > 0 && (
                <div className="py-10 md:py-2XL md:px-5">
                  <h2 className="text-center uppercase font-bstSpyre text-base-b mb-LG md:mb-DLG">{CD_STATE.searchspring.searchResultRecommendationHeading}</h2>
                  <ProductCarousel
                    classSlider="md-max:w-[230px] md:min-h-[500px]"
                    products={recommendations}
                    source="searchspring"
                    spaceBetween={0}
                    slidesPerView="auto"
                    breakpoints={
                      {
                        768: { slidesPerView: 3, spaceBetween: 20 },
                        1024: { slidesPerView: 4, spaceBetween: 20 } 
                      }
                    }
                  />
                </div>
              )
            )
          }
        </div>
      )
    }

    // if error display error
    if (error) {
      return (
        <div className="mx-5 mt-4 text-xl text-center font-bstSpyre">
          Sorry, search is not working right now. Please try again later or contact us if this issue persists.
        </div>
      )
    }

    // TODO: implement skeleton loaders
    return <div className="min-h-[1080px]"></div>
  }

  return <ComponentHandler />
}

export default SearchPageInit
